import { Check, ArrowRight } from "lucide-react";
import { SERVICES } from "../../data/content";
import { Reveal, scrollToId } from "../../lib/motion";

const ServiceCard = ({ s }) => {
  const isSage = s.tone === "sage";
  return (
    <article
      id={s.id}
      data-testid={`service-${s.id}`}
      className={`scroll-mt-28 rounded-[2rem] p-8 lg:p-12 border shadow-soft flex flex-col ${
        isSage ? "bg-sage-dark text-cream border-sage-deep" : "bg-beige-light text-ink border-beige/60"
      }`}
    >
      <p className={`overline mb-4 ${isSage ? "text-sage-light" : "text-sage-dark"}`}>{s.label}</p>
      <h3 className={`font-serif text-3xl lg:text-4xl leading-tight ${isSage ? "text-cream" : "text-ink"}`}>
        {s.tagline}
      </h3>
      <p className={`mt-5 leading-relaxed ${isSage ? "text-sage-light/90" : "text-ink-soft"}`}>
        {s.description}
      </p>

      <div className={`mt-7 rounded-2xl p-5 ${isSage ? "bg-sage-deep/60" : "bg-white/70"}`}>
        <p className={`text-xs uppercase tracking-widest mb-2 ${isSage ? "text-sage-light" : "text-ink-muted"}`}>Ideal for</p>
        <p className={`text-sm leading-relaxed ${isSage ? "text-cream/90" : "text-ink-soft"}`}>{s.idealClient}</p>
      </div>

      <ul className="mt-7 space-y-3">
        {s.included.map((item) => (
          <li key={item} className="flex items-start gap-3">
            <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${isSage ? "bg-sage-light/25" : "bg-sage-light"}`}>
              <Check size={12} strokeWidth={2.5} className={isSage ? "text-cream" : "text-sage-dark"} />
            </span>
            <span className={`text-[0.95rem] ${isSage ? "text-cream/90" : "text-ink-soft"}`}>{item}</span>
          </li>
        ))}
      </ul>

      <p className={`mt-7 text-sm italic ${isSage ? "text-sage-light" : "text-ink-muted"}`}>{s.session}</p>

      <div className={`mt-6 pt-6 border-t space-y-3 ${isSage ? "border-sage-light/20" : "border-beige/70"}`}>
        {s.prices.map((p) => (
          <div key={p.name} className="flex items-baseline justify-between gap-4" data-testid="price-row">
            <div>
              <p className={`text-[0.95rem] font-medium ${isSage ? "text-cream" : "text-ink"}`}>{p.name}</p>
              <p className={`text-xs ${isSage ? "text-sage-light/80" : "text-ink-muted"}`}>{p.note}</p>
            </div>
            <span className={`font-serif text-2xl shrink-0 ${isSage ? "text-cream" : "text-sage-dark"}`}>{p.price}</span>
          </div>
        ))}
      </div>

      <button
        onClick={() => scrollToId("contact")}
        data-testid={`service-cta-${s.id}`}
        className={`group mt-9 inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-sm font-medium tracking-wide transition-all duration-300 active:scale-[0.98] ${
          isSage ? "bg-cream text-sage-dark hover:bg-white" : "bg-sage-dark text-cream hover:bg-sage-deep"
        }`}
      >
        {isSage ? "Enquire about counselling" : "Enquire about supervision"}
        <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </article>
  );
};

export const Services = () => {
  return (
    <section className="py-24 lg:py-36 bg-cream" aria-labelledby="services-heading">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-16">
          <Reveal><p className="overline text-sage-dark mb-5">Services & fees</p></Reveal>
          <Reveal delay={0.05}>
            <h2 id="services-heading" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
              Warm support, thoughtfully priced.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-ink-soft leading-relaxed">
              Two ways I can help — one-to-one counselling, and clinical supervision for fellow
              practitioners. Everything begins with a free, no-obligation conversation.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          <Reveal><ServiceCard s={SERVICES.counselling} /></Reveal>
          <Reveal delay={0.1}><ServiceCard s={SERVICES.supervision} /></Reveal>
        </div>
      </div>
    </section>
  );
};
